/**
 * Grid Drag Integration Service
 * Provides utilities for seamless integration between CSS Grid and React DnD
 */

import { DragSourceMonitor, DropTargetMonitor } from 'react-dnd';
import { 
  GridPosition, 
  gridDragCache, 
  restoreShiftGridPosition,
  calculateDragPreview,
  getDropZoneFromMousePosition,
  DragPreviewInfo
} from './css-grid-positioning';
import { DayShift } from '@/types';

/**
 * Enhanced drag state manager for CSS Grid compatibility
 */
export class GridDragIntegration {
  private static instance: GridDragIntegration;
  private dragStates = new Map<string, {
    originalPosition: GridPosition;
    element: HTMLElement;
    isGridPositioned: boolean;
    dragStartTime: number;
  }>();

  private constructor() {}

  static getInstance(): GridDragIntegration {
    if (!GridDragIntegration.instance) {
      GridDragIntegration.instance = new GridDragIntegration();
    }
    return GridDragIntegration.instance;
  }

  /**
   * Initialize drag operation with grid position tracking
   */
  startDrag(
    shiftId: string,
    element: HTMLElement,
    originalGridColumn: string,
    originalGridRow: number,
    isGridPositioned: boolean = true
  ): void {
    const dragState = {
      originalPosition: {
        gridColumn: originalGridColumn,
        gridRow: originalGridRow
      },
      element,
      isGridPositioned,
      dragStartTime: Date.now()
    };

    this.dragStates.set(shiftId, dragState);

    // Add visual drag state
    if (isGridPositioned) {
      element.style.transition = 'none'; // Disable transitions during drag
      element.classList.add('dragging-grid-item');
    }
  }

  /**
   * Handle successful drop with grid position update
   */
  completeDrag(
    shiftId: string,
    newGridColumn?: string,
    newGridRow?: number
  ): void {
    const dragState = this.dragStates.get(shiftId);
    if (!dragState) return;

    const { element, isGridPositioned } = dragState;

    if (isGridPositioned && newGridColumn && newGridRow) {
      // Update to new grid position
      element.style.gridColumn = newGridColumn;
      element.style.gridRow = newGridRow.toString();
      element.style.position = 'relative';
      element.style.transform = '';
    }

    // Clean up drag styles
    element.style.transition = '';
    element.classList.remove('dragging-grid-item');
    this.dragStates.delete(shiftId);
  }

  /**
   * Cancel drag and restore original position
   */
  cancelDrag(shiftId: string): void {
    const dragState = this.dragStates.get(shiftId);
    if (!dragState) return;

    const { originalPosition, element, isGridPositioned } = dragState;

    if (isGridPositioned) {
      // Restore original grid position
      element.style.gridColumn = originalPosition.gridColumn;
      element.style.gridRow = originalPosition.gridRow.toString();
      element.style.position = 'relative';
      element.style.left = '';
      element.style.top = '';
      element.style.transform = '';
      element.style.zIndex = '';
    }

    // Clean up drag styles
    element.style.transition = '';
    element.classList.remove('dragging-grid-item');
    this.dragStates.delete(shiftId);
  }

  /**
   * Get current drag performance metrics
   */
  getDragMetrics(shiftId: string): { duration: number; isActive: boolean } | null {
    const dragState = this.dragStates.get(shiftId);
    if (!dragState) return null;

    return {
      duration: Date.now() - dragState.dragStartTime,
      isActive: true
    };
  }

  /**
   * Clean up all drag states (useful on unmount)
   */
  cleanup(): void {
    for (const [shiftId] of this.dragStates) {
      this.cancelDrag(shiftId);
    }
    gridDragCache.clearCache();
  }
}

/**
 * React DnD integration hooks for CSS Grid
 */

/**
 * Enhanced drag configuration for grid-positioned shifts
 */
export const createGridDragSource = (
  shift: DayShift,
  gridColumn: string | undefined,
  gridRow: number | undefined,
  isDraggableInPanel: boolean = false
) => {
  const dragIntegration = GridDragIntegration.getInstance();

  return {
    type: 'PLACED_SHIFT',
    item: {
      shiftId: shift.id,
      title: shift.title || 'Untitled',
      startTime: shift.startTime,
      endTime: shift.endTime,
      originalGridColumn: gridColumn,
      originalGridRow: gridRow,
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    canDrag: isDraggableInPanel && !shift.isNew,
    begin: () => {
      const element = document.querySelector(`[data-shift-id="${shift.id}"]`) as HTMLElement;
      if (element && gridColumn && gridRow) {
        dragIntegration.startDrag(
          shift.id,
          element,
          gridColumn,
          gridRow,
          true
        );
      }
      return {
        shiftId: shift.id,
        title: shift.title || 'Untitled',
        startTime: shift.startTime,
        endTime: shift.endTime,
        originalGridColumn: gridColumn,
        originalGridRow: gridRow,
        element
      };
    },
    end: (item: any, monitor: DragSourceMonitor) => {
      const result = monitor.getDropResult();
      if (result) {
        // Successful drop - let the drop handler manage position
        dragIntegration.completeDrag(shift.id);
      } else {
        // Cancelled drag - restore original position
        dragIntegration.cancelDrag(shift.id);
      }
    },
  };
};

/**
 * Enhanced drop configuration for grid-positioned cells
 */
export const createGridDropTarget = (
  dayKey: string,
  dayDate: Date,
  cellTime: Date,
  hourCellWidthPx: number,
  hideDayColumn: boolean = false,
  onRepositionShift?: (shiftId: string, newStartTime: Date, newEndTime: Date) => Promise<void>
) => {
  return {
    accept: ['SHIFT_TEMPLATE', 'PLACED_SHIFT', 'EMPLOYEE'],
    drop: async (item: any, monitor: DropTargetMonitor) => {
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return { result: 'no_offset' };

      const dropContainer = document.querySelector(`[data-day-key="${dayKey}"]`) as HTMLElement;
      if (!dropContainer) return { result: 'no_container' };

      try {
        const dropZoneInfo = getDropZoneFromMousePosition(
          clientOffset.x,
          clientOffset.y,
          dropContainer,
          dayDate,
          new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 0, 0),
          24,
          hourCellWidthPx,
          hideDayColumn
        );

        if (monitor.getItemType() === 'PLACED_SHIFT' && onRepositionShift) {
          const originalDurationMs = item.endTime.getTime() - item.startTime.getTime();
          const newEndTime = new Date(dropZoneInfo.snapToTime.getTime() + originalDurationMs);
          
          await onRepositionShift(item.shiftId, dropZoneInfo.snapToTime, newEndTime);
        }

        return { result: 'success', snapToTime: dropZoneInfo.snapToTime };
      } catch (error) {
        console.error('Grid drop error:', error);
        return { result: 'error' };
      }
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  };
};

/**
 * Performance optimization utilities
 */

/**
 * Debounced drag feedback to reduce render frequency
 */
export class DragFeedbackThrottler {
  private throttleTimeouts = new Map<string, NodeJS.Timeout>();
  private readonly THROTTLE_MS = 16; // 60fps

  throttleUpdate(key: string, updateFn: () => void): void {
    const existingTimeout = this.throttleTimeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(() => {
      updateFn();
      this.throttleTimeouts.delete(key);
    }, this.THROTTLE_MS);

    this.throttleTimeouts.set(key, timeout);
  }

  cleanup(): void {
    for (const timeout of this.throttleTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.throttleTimeouts.clear();
  }
}

// Export singleton instances
export const gridDragIntegration = GridDragIntegration.getInstance();
export const dragFeedbackThrottler = new DragFeedbackThrottler();

/**
 * CSS classes for grid drag states
 */
export const GRID_DRAG_STYLES = `
  .dragging-grid-item {
    z-index: 1000;
    opacity: 0.8;
    transition: none !important;
    cursor: grabbing;
  }
  
  .grid-drop-target {
    background-color: rgba(59, 130, 246, 0.1);
    border: 2px dashed rgba(59, 130, 246, 0.5);
    border-radius: 4px;
  }
  
  .grid-drop-invalid {
    background-color: rgba(239, 68, 68, 0.1);
    border: 2px dashed rgba(239, 68, 68, 0.5);
    border-radius: 4px;
  }
`;